import { ArrowRight, Check, Copy, Minus, Plus } from 'lucide-react';
import * as React from 'react';
import { toast } from 'sonner';
import { DataTable, TabelaPainel, type Coluna } from '@/components/patterns/data-table';
import { FilterBar, FilterBusca, FilterLimpar, FilterSelect } from '@/components/patterns/filter-bar';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { config } from '@/data/config';
import { useAcesso } from '@/hooks/use-acesso';
import type { Artefato, DiffAnterior } from '@/data/types';
import { indexarDiff, resumirDiff, type MudancaLinha } from '@/lib/diff';
import { coordenadaNexus, plural, urlNexus } from '@/lib/format';

/**
 * Manifesto da versão: o que o fastupdate resolve no Nexus para este
 * instalador, com o que mudou em relação à versão anterior do mesmo branch.
 *
 * O nome mostrado é o do Nexus (`artefato`), em fonte mono porque é o que se
 * procura no Nexus; o botão de copiar leva o LINK de download do artefato no
 * Nexus (a coordenada Maven `grupo:artefato:tipo:versao` fica no tooltip).
 */

interface Linha extends Artefato {
  mudanca: MudancaLinha;
}

function BotaoCopiar({ item }: { item: Artefato }) {
  const link = urlNexus(item, config.nexusUrl);
  const coordenada = coordenadaNexus(item);
  const [copiado, setCopiado] = React.useState(false);

  async function copiar() {
    try {
      await navigator.clipboard.writeText(link);
      setCopiado(true);
      toast.success('Link do Nexus copiado.', { description: link });
      window.setTimeout(() => setCopiado(false), 1500);
    } catch {
      toast.error('O navegador não permitiu copiar. Selecione o link no tooltip.');
    }
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => void copiar()}
          aria-label={`Copiar link do Nexus de ${item.artefato} ${item.versao}`}
          className="inline-flex size-8 items-center justify-center rounded-button text-icon transition-colors duration-fast ease-standard hover:bg-bg-subtle hover:text-on-surface"
        >
          {copiado ? (
            <Check className="size-4 text-success" aria-hidden="true" />
          ) : (
            <Copy className="size-4" aria-hidden="true" />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent className="flex max-w-none flex-col gap-0.5">
        <span className="select-all font-mono">{link}</span>
        <span className="font-mono text-micro opacity-70">{coordenada}</span>
      </TooltipContent>
    </Tooltip>
  );
}

export function Manifesto({ artefatos, diff }: { artefatos: Artefato[]; diff: DiffAnterior }) {
  // O link do Nexus é interno: quem não é @nasajon.com.br não recebe o botão de copiar.
  const { colaborador } = useAcesso();
  const [busca, setBusca] = React.useState('');
  const [tipo, setTipo] = React.useState<string | null>(null);
  const [soMudancas, setSoMudancas] = React.useState(false);

  const indice = React.useMemo(() => indexarDiff(diff), [diff]);
  const linhas: Linha[] = React.useMemo(
    () => artefatos.map((item) => ({ ...item, mudanca: indice.mudanca(item) })),
    [artefatos, indice],
  );
  const tipos = React.useMemo(() => [...new Set(artefatos.map((item) => item.tipo))].sort(), [artefatos]);

  const termo = busca.trim().toLowerCase();
  const visiveis = linhas.filter(
    (item) =>
      (!termo || item.artefato.toLowerCase().includes(termo)) &&
      (!tipo || item.tipo === tipo) &&
      (!soMudancas || item.mudanca !== null),
  );
  const removidos = diff.removidos.filter(
    (item) => (!termo || item.artefato.toLowerCase().includes(termo)) && (!tipo || item.tipo === tipo),
  );
  const filtrosAtivos = [termo, tipo, soMudancas].filter(Boolean).length;

  const colunas: Array<Coluna<Linha>> = [
    {
      id: 'artefato',
      cabecalho: 'Artefato (Nexus)',
      ordenarPor: (item) => item.artefato.toLowerCase(),
      celula: (item) => <span className="break-all font-mono text-cell">{item.artefato}</span>,
    },
    {
      id: 'tipo',
      cabecalho: 'Tipo',
      largura: '72px',
      ordenarPor: (item) => item.tipo,
      celula: (item) => (
        <Badge tone="neutral" className="font-mono font-normal">
          {item.tipo}
        </Badge>
      ),
    },
    {
      id: 'versao',
      cabecalho: 'Versão',
      ordenarPor: (item) => item.versao,
      celula: (item) =>
        item.mudanca?.tipo === 'alterado' ? (
          <span className="inline-flex flex-wrap items-center gap-1.5 font-mono">
            <span className="text-on-surface-muted line-through decoration-1">{item.mudanca.de}</span>
            <ArrowRight className="size-3 text-icon" aria-label="para" />
            <span className="font-bold">{item.versao}</span>
          </span>
        ) : (
          <span className="font-mono">{item.versao}</span>
        ),
    },
    {
      id: 'mudanca',
      cabecalho: 'Mudança',
      largura: '112px',
      ordenarPor: (item) => (item.mudanca ? (item.mudanca.tipo === 'adicionado' ? 0 : 1) : 2),
      celula: (item) =>
        item.mudanca?.tipo === 'alterado' ? (
          <Badge tone="info" icon={<ArrowRight aria-hidden="true" />}>
            Alterado
          </Badge>
        ) : item.mudanca?.tipo === 'adicionado' ? (
          <Badge tone="success" icon={<Plus aria-hidden="true" />}>
            Novo
          </Badge>
        ) : (
          <span className="text-on-surface-muted">—</span>
        ),
    },
    {
      id: 'copiar',
      cabecalho: <span className="sr-only">Coordenada</span>,
      largura: '48px',
      alinhamento: 'right',
      celula: (item) => <BotaoCopiar item={item} />,
    },
  ];
  const colunasVisiveis = colunas.filter((coluna) => coluna.id !== 'copiar' || colaborador);

  return (
    <TabelaPainel
      titulo="Manifesto"
      contagem={plural(artefatos.length, 'artefato', 'artefatos')}
      filtros={
        <div className="flex flex-col gap-3">
          <p className="text-cell text-on-surface-muted">{resumirDiff(diff)}</p>
          <FilterBar>
            <FilterBusca
              id="manifesto-busca"
              label="Buscar artefato"
              valor={busca}
              aoMudar={setBusca}
              placeholder="nsjPersona, Database…"
            />
            {tipos.length > 1 ? (
              <FilterSelect
                id="manifesto-tipo"
                label="Tipo"
                valor={tipo}
                aoMudar={setTipo}
                opcoes={tipos.map((valor) => ({ valor, rotulo: valor }))}
                className="min-w-28"
              />
            ) : null}
            {!indice.semAnterior ? (
              <label className="flex h-10 items-center gap-2 text-label">
                <Switch checked={soMudancas} onCheckedChange={setSoMudancas} aria-label="Mostrar só o que mudou" />
                Só o que mudou
              </label>
            ) : null}
            <FilterLimpar
              ativo={filtrosAtivos > 0}
              quantidade={filtrosAtivos}
              aoLimpar={() => {
                setBusca('');
                setTipo(null);
                setSoMudancas(false);
              }}
            />
          </FilterBar>
        </div>
      }
    >
      <DataTable
        itens={visiveis}
        colunas={colunasVisiveis}
        chave={(item) => `${item.grupo}:${item.artefato}:${item.tipo}`}
        estado="pronto"
        rotulo="Artefatos do manifesto"
        classeLinha={(item) => (item.mudanca ? 'bg-info-wash' : undefined)}
        vazio={
          artefatos.length === 0
            ? { titulo: 'Manifesto vazio', descricao: 'O Jenkins não enviou artefatos para esta versão.' }
            : { titulo: 'Nenhum artefato com estes filtros', descricao: 'Limpe a busca ou o filtro de tipo.' }
        }
      />
      {removidos.length > 0 ? (
        <div className="border-t border-border px-4 py-3">
          <p className="mb-2 flex items-center gap-1.5 text-label text-danger">
            <Minus className="size-3.5" aria-hidden="true" />
            Removidos desde {diff.versao_anterior}
          </p>
          <ul className="flex flex-wrap gap-x-4 gap-y-1">
            {removidos.map((item) => (
              <li key={`${item.artefato}:${item.tipo}`} className="font-mono text-cell text-on-surface-muted">
                <span className="line-through decoration-1">{item.artefato}</span> · {item.tipo} · {item.versao}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </TabelaPainel>
  );
}
